import { useEffect, useRef } from 'react';
import { cn } from '../../lib/utils';
import type { CompetitionCategory } from '../../types/competition';

interface RoundCategoryTabsProps {
  categories: CompetitionCategory[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  className?: string;
}

/**
 * The classes a round runs, as the tabs its blocks are read through.
 *
 * Scrolling rather than sharing the row (the crag's sector tabs do the same):
 * a youth stage runs eight classes, and squeezed into a phone's width every
 * name would be cut short. The open tab is scrolled into view whenever it
 * changes — a swipe on the content below picks the next class, which may well
 * be off the edge of the strip.
 */
export function RoundCategoryTabs({ categories, selectedId, onSelect, className }: RoundCategoryTabsProps) {
  const selectedRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    selectedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, [selectedId]);

  return (
    <div
      role="tablist"
      aria-orientation="horizontal"
      className={cn('flex gap-1 overflow-x-auto rounded-lg bg-muted p-1 [scrollbar-width:none]', className)}
    >
      {categories.map((category) => {
        const isSelected = category.id === selectedId;
        return (
          <button
            key={category.id}
            ref={isSelected ? selectedRef : undefined}
            type="button"
            role="tab"
            aria-selected={isSelected}
            onClick={() => onSelect(category.id)}
            className={cn(
              'shrink-0 whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium transition-colors',
              isSelected
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {category.name}
          </button>
        );
      })}
    </div>
  );
}
